<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { pipelineReport, exportPipelineReport } from '../api';
import { useCrmStore } from '../store';
import { formatMoney } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useCrmStore();
const agents = useMapGetter('agents/getVerifiedAgents');

const pipelineId = computed(() => Number(route.params.pipelineId));
const isLoading = ref(false);
const report = ref(null);

// format in LOCAL time (toISOString would shift the day for UTC-3 evenings)
const toInput = date =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
const since = ref(toInput(new Date(Date.now() - 29 * 864e5)));
const until = ref(toInput(new Date()));
const assigneeId = ref('');
const hasError = ref(false);
const invalidRange = computed(() => since.value > until.value);

const agentOptions = computed(() => [
  { value: '', label: t('WASAPI_CRM.REPORT.ALL') },
  ...(agents.value || []).map(a => ({ value: a.id, label: a.name })),
]);
const pipeline = computed(() =>
  store.pipelines.find(p => p.id === pipelineId.value)
);

// The API compares absolute instants, so the local day boundaries go out with their offset —
// a bare "23:59:59" is read as UTC by the server and drops the evening of the last day.
const rangeParams = () => ({
  since: new Date(`${since.value}T00:00:00`).toISOString(),
  until: new Date(`${until.value}T23:59:59.999`).toISOString(),
  assignee_id: assigneeId.value || undefined,
});

const load = async () => {
  if (invalidRange.value) return;
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await pipelineReport(pipelineId.value, rangeParams());
    report.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const exportCsv = async () => {
  const { data } = await exportPipelineReport(pipelineId.value, rangeParams());
  const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `crm-report-${pipelineId.value}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

onMounted(async () => {
  if (!store.pipelines.length) await store.fetchPipelines();
  load();
});

const money = cents => formatMoney(cents, 'BRL', locale.value);
const tiles = computed(() => {
  const tt = report.value?.totals || {};
  return [
    { label: t('WASAPI_CRM.REPORT.ENTRIES'), value: tt.entries ?? 0 },
    { label: t('WASAPI_CRM.REPORT.OPEN_NOW'), value: tt.open_now ?? 0 },
    {
      label: t('WASAPI_CRM.REPORT.WON'),
      value: tt.won ?? 0,
      accent: 'text-n-teal-11',
    },
    {
      label: t('WASAPI_CRM.REPORT.WON_AMOUNT'),
      value: money(tt.won_amount_cents || 0),
      accent: 'text-n-teal-11',
    },
    {
      label: t('WASAPI_CRM.REPORT.LOST'),
      value: tt.lost ?? 0,
      accent: 'text-n-ruby-11',
    },
    {
      label: t('WASAPI_CRM.REPORT.CONVERSION'),
      value: tt.conversion_rate == null ? '—' : `${tt.conversion_rate}%`,
    },
    { label: t('WASAPI_CRM.REPORT.MOVEMENTS'), value: tt.movements ?? 0 },
  ];
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="w-full max-w-5xl px-6 py-6 mx-auto flex flex-col gap-6">
      <header class="flex flex-wrap items-end justify-between gap-4">
        <div class="flex flex-col gap-1">
          <Button
            :label="t('WASAPI_CRM.REPORT.BACK')"
            icon="i-lucide-arrow-left"
            variant="link"
            color="slate"
            size="sm"
            class="self-start"
            @click="router.push({ name: 'wasapi_crm_board' })"
          />
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('WASAPI_CRM.REPORT.TITLE') }}
            <span v-if="pipeline" class="text-n-slate-11">
              {{ `· ${pipeline.name}` }}
            </span>
          </h1>
        </div>
        <div class="flex flex-wrap items-end gap-2">
          <Input
            v-model="since"
            type="date"
            :label="t('WASAPI_CRM.REPORT.SINCE')"
          />
          <Input
            v-model="until"
            type="date"
            :label="t('WASAPI_CRM.REPORT.UNTIL')"
          />
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">{{
              t('WASAPI_CRM.REPORT.ASSIGNEE')
            }}</span>
            <div class="w-48 shrink-0">
              <ComboBox v-model="assigneeId" :options="agentOptions" />
            </div>
          </div>
          <Button
            :label="t('WASAPI_CRM.REPORT.APPLY')"
            color="blue"
            :is-loading="isLoading"
            :disabled="invalidRange"
            @click="load"
          />
          <Button
            :label="t('WASAPI_CRM.REPORT.EXPORT_CSV')"
            icon="i-lucide-download"
            variant="faded"
            color="slate"
            :disabled="invalidRange"
            @click="exportCsv"
          />
        </div>
      </header>

      <p v-if="invalidRange" class="text-xs text-n-ruby-11">
        {{ t('WASAPI_CRM.REPORT.INVALID_RANGE') }}
      </p>
      <div
        v-if="hasError"
        class="flex items-center gap-2 p-3 text-sm rounded-xl bg-n-ruby-3/40 text-n-ruby-11"
      >
        {{ t('WASAPI_CRM.REPORT.LOAD_ERROR') }}
        <Button
          :label="t('WASAPI_CRM.REPORT.RETRY')"
          variant="faded"
          color="ruby"
          size="xs"
          @click="load"
        />
      </div>
      <div
        v-if="report?.truncated"
        class="flex items-center gap-2 p-3 text-xs rounded-xl bg-n-amber-3/50 text-n-amber-11"
      >
        {{ t('WASAPI_CRM.REPORT.TRUNCATED') }}
      </div>
      <div v-if="isLoading && !report" class="flex justify-center py-10">
        <Spinner />
      </div>
      <template v-else-if="report">
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          <div
            v-for="tile in tiles"
            :key="tile.label"
            class="flex flex-col gap-1 p-3 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
          >
            <span class="text-xs text-n-slate-11">{{ tile.label }}</span>
            <span
              class="text-lg font-semibold text-n-slate-12 truncate"
              :class="tile.accent"
              >{{ tile.value }}</span
            >
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('WASAPI_CRM.REPORT.STAGES') }}
          </h2>
          <div
            class="overflow-x-auto rounded-xl outline outline-1 outline-n-weak"
          >
            <table class="w-full text-sm">
              <thead class="text-xs text-left text-n-slate-11 bg-n-alpha-1">
                <tr>
                  <th class="px-3 py-2">{{ t('WASAPI_CRM.REPORT.STAGE') }}</th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.CURRENT') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.AMOUNT') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.ENTERED') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.AVG_HOURS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="stage in report.stages"
                  :key="stage.id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 flex items-center gap-2 text-n-slate-12">
                    <span
                      class="size-2.5 rounded-full"
                      :style="{ backgroundColor: stage.color }"
                    />{{ stage.name }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ stage.current_count }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ money(stage.current_amount_cents) }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ stage.entered_in_period }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{
                      stage.avg_hours_in_stage == null
                        ? '—'
                        : t('WASAPI_CRM.REPORT.HOURS', {
                            count: stage.avg_hours_in_stage,
                          })
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div class="flex flex-col gap-2">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ t('WASAPI_CRM.REPORT.BY_ASSIGNEE') }}
            </h2>
            <p
              v-if="!report.by_assignee.length"
              class="text-sm text-n-slate-11"
            >
              {{ t('WASAPI_CRM.REPORT.EMPTY') }}
            </p>
            <table
              v-else
              class="w-full text-sm rounded-xl outline outline-1 outline-n-weak"
            >
              <thead class="text-xs text-left text-n-slate-11 bg-n-alpha-1">
                <tr>
                  <th class="px-3 py-2">
                    {{ t('WASAPI_CRM.REPORT.ASSIGNEE') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.CREATED') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.WON') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.WON_AMOUNT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in report.by_assignee"
                  :key="row.assignee_id || 'none'"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ row.name || t('WASAPI_CRM.REPORT.UNASSIGNED') }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ row.created }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ row.won }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ money(row.won_amount_cents) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="flex flex-col gap-2">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ t('WASAPI_CRM.REPORT.BY_ORIGIN') }}
            </h2>
            <p v-if="!report.by_origin.length" class="text-sm text-n-slate-11">
              {{ t('WASAPI_CRM.REPORT.EMPTY') }}
            </p>
            <table
              v-else
              class="w-full text-sm rounded-xl outline outline-1 outline-n-weak"
            >
              <thead class="text-xs text-left text-n-slate-11 bg-n-alpha-1">
                <tr>
                  <th class="px-3 py-2">
                    {{ t('WASAPI_CRM.REPORT.BY_ORIGIN') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.CREATED') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in report.by_origin"
                  :key="row.inbox_id || 'none'"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 text-n-slate-12">{{ row.name }}</td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ row.created }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="flex flex-col gap-2">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ t('WASAPI_CRM.REPORT.BY_CLOSE_REASON') }}
            </h2>
            <p
              v-if="!(report.by_close_reason || []).length"
              class="text-sm text-n-slate-11"
            >
              {{ t('WASAPI_CRM.REPORT.EMPTY') }}
            </p>
            <table
              v-else
              class="w-full text-sm rounded-xl outline outline-1 outline-n-weak"
            >
              <thead class="text-xs text-left text-n-slate-11 bg-n-alpha-1">
                <tr>
                  <th class="px-3 py-2">
                    {{ t('WASAPI_CRM.REPORT.BY_CLOSE_REASON') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.COUNT') }}
                  </th>
                  <th class="px-3 py-2 text-right">
                    {{ t('WASAPI_CRM.REPORT.AMOUNT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in report.by_close_reason"
                  :key="`${row.kind}-${row.close_reason_id || 'none'}`"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 text-n-slate-12">
                    <span
                      class="inline-block px-1.5 py-0.5 mr-1.5 text-[0.625rem] rounded"
                      :class="
                        row.kind === 'won'
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-ruby-3 text-n-ruby-11'
                      "
                    >
                      {{ t(`WASAPI_CRM.REPORT.${row.kind.toUpperCase()}`) }}
                    </span>
                    {{ row.name }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ row.count }}
                  </td>
                  <td class="px-3 py-2 text-right tabular-nums">
                    {{ money(row.amount_cents) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>
